import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, MessageCircle, Clock, Users, Sparkles, ShieldCheck } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, CTAStrip } from './shared/blocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BRAND, PILOT_TERMS } from './content';

const PILOT_WA = 'Hi Founder! I want to bring ActanonEdu to my school via the 30-day pilot program. Please share details.';

export default function SchoolPilotProgramPage() {
  return (
    <MarketingLayout>
      <SEO
        title="School Pilot Program | 30-day free pilot for Indian schools"
        description="A 30-day free pilot of ActanonEdu for your school. Up to 50 teachers. Founder-led onboarding. Custom blueprint match. Full School ERP access during the pilot."
        path="/school-pilot-program"
        keywords={['school pilot program', 'school ERP trial', 'AI for schools India', 'free school software trial']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'School Pilot Program', url: '/school-pilot-program' }]}
      />
      <PageHero
        eyebrow={<><Badge className="bg-rose-600 text-white border-0">🚀 Pilot Program Open</Badge></>}
        title="The ActanonEdu"
        accent="School Pilot Program."
        subtitle="30 days. Up to 50 teachers. Founder-led onboarding. Custom blueprint to match your school's paper format. Applications open till 30th June."
        testid="pilot-hero"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={BRAND.whatsappLink(PILOT_WA)} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-7 font-semibold w-full sm:w-auto" data-testid="pilot-cta-wa">
              <MessageCircle className="mr-1.5 h-4 w-4" /> Apply via WhatsApp
            </Button>
          </a>
          <Link to="/contact" className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="h-12 px-7 font-semibold w-full sm:w-auto border-2" data-testid="pilot-cta-contact">
              Contact Form
            </Button>
          </Link>
        </div>
      </PageHero>

      <Section>
        <SectionHeader eyebrow="What you get" title="A real pilot — not a demo." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Clock,       title: '30-day free access', desc: 'Strictly 30 days starting from your activation date.' },
            { icon: Users,       title: 'Up to 50 teachers',  desc: 'Full teacher tools + content engine for every teacher.' },
            { icon: Sparkles,    title: 'ERP modules unlocked', desc: 'Attendance, fees, communication, student records.' },
            { icon: ShieldCheck, title: 'Founder onboarding', desc: 'Direct WhatsApp line to founder during the pilot.' },
          ].map((it) => (
            <div key={it.title} className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5">
              <it.icon className="h-5 w-5 text-mk-blue dark:text-mk-cyan mb-2" />
              <div className="font-bold">{it.title}</div>
              <div className="text-sm text-mk-muted dark:text-slate-300 mt-1 leading-relaxed">{it.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeader eyebrow="How it works" title="From application to onboarding in under a week." />
        <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { step: '1', title: 'Apply', desc: 'Send a WhatsApp message — name, school, role, city.' },
            { step: '2', title: 'Founder call',  desc: 'A 20-min call to understand fit, board, blueprint.' },
            { step: '3', title: 'Setup',  desc: 'We seed your school, classes, sections, and 5 teachers to start.' },
            { step: '4', title: 'Go live', desc: 'Teachers begin generating. You receive a weekly usage report.' },
          ].map((s) => (
            <li key={s.step} className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5 relative">
              <div className="absolute -top-3 -left-3 h-10 w-10 rounded-xl bg-gradient-to-br from-mk-blue to-mk-blue text-white grid place-items-center font-bold shadow-md">
                {s.step}
              </div>
              <div className="font-bold pl-4">{s.title}</div>
              <div className="text-sm text-mk-muted dark:text-slate-300 mt-1 pl-4 leading-relaxed">{s.desc}</div>
            </li>
          ))}
        </ol>
      </Section>

      <Section>
        <SectionHeader eyebrow="Pilot terms" title="The fine print — in plain English." subtitle="Participation in the pilot constitutes agreement to these terms." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PILOT_TERMS.map((t) => (
            <div key={t.title} className="rounded-xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5">
              <div className="font-bold mb-2">{t.title}</div>
              <ul className="space-y-1.5 text-sm text-mk-muted dark:text-slate-300">
                {t.items.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-1 shrink-0" /> {i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <CTAStrip
        title="Bring ActanonEdu to your school."
        subtitle="30-day pilot. Founder onboarding. Zero risk."
        primary={{ label: 'Sign up free', href: '/signup' }}
        secondary={{ label: 'Apply via WhatsApp', wa: PILOT_WA }}
        testid="pilot-final-cta"
      />
    </MarketingLayout>
  );
}
